import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService, HttpMethod } from '../api/api.service';
import { AuthEventsService, AuthEventType } from '../auth/auth-events.service';
import { OrganizationGroupService } from '../organization-group/organization-group.service';
import { DUMMY_SEPARATION_REVIEW_RESPONSE } from './dummy-data';

// Set to true to use dummy data instead of API calls
// const USE_DUMMY_DATA = true;
const USE_DUMMY_DATA = false;

export interface SeparationReviewSummary {
  documentId: string;
  originalFilename?: string;
  parentDocumentStatus: string;
  stepFunctionStatus: string;
  organization: string;
  household?: string;
  totalPages?: number;
  childDocumentCount?: number;
  taskTokenCreatedAt: string;
  taskTokenExpiresAt: string;
}

export interface SeparationReviewListResponse {
  documents: SeparationReviewSummary[];
  totalCount?: number;
}

export interface ClassificationReviewSummary {
  documentId: string;
  originalFilename?: string;
  status: string;
  organization: string;
  householdId?: string;
  createdAt: string;
}

export interface ClassificationReviewListResponse {
  documents: ClassificationReviewSummary[];
  totalCount?: number;
}

export interface PendingReviewsState {
  separation_reviews: SeparationReviewSummary[];
  classification_reviews: ClassificationReviewSummary[];
  separation_review_count: number;
  classification_review_count: number;
  total_count: number;
  is_loading: boolean;
  last_fetched_at: Date | null;
  selected_household_id: number | null;
}

const INITIAL_STATE: PendingReviewsState = {
  separation_reviews: [],
  classification_reviews: [],
  separation_review_count: 0,
  classification_review_count: 0,
  total_count: 0,
  is_loading: false,
  last_fetched_at: null,
  selected_household_id: null,
};

@Injectable({
  providedIn: 'root'
})
export class PendingReviewsService implements OnDestroy {
  private state_subject = new BehaviorSubject<PendingReviewsState>(INITIAL_STATE);
  private subscriptions: Subscription[] = [];

  // Public observables
  public state$: Observable<PendingReviewsState> = this.state_subject.asObservable();

  public pending_review_count$: Observable<number> = this.state$.pipe(
    map(state => state.total_count)
  );

  public separation_reviews$: Observable<SeparationReviewSummary[]> = this.state$.pipe(
    map(state => state.separation_reviews)
  );

  public classification_reviews$: Observable<ClassificationReviewSummary[]> = this.state$.pipe(
    map(state => state.classification_reviews)
  );

  public is_loading$: Observable<boolean> = this.state$.pipe(
    map(state => state.is_loading)
  );

  constructor(
    private api_service: ApiService,
    private auth_events_service: AuthEventsService,
    private organization_group_service: OrganizationGroupService
  ) {
    this.listenToAuthEvents();
    this.listenToFirmContextChanges();
  }

  private listenToAuthEvents(): void {
    this.subscriptions.push(
      this.auth_events_service.authEvents$.subscribe(event => {
        switch (event.type) {
          case AuthEventType.USER_DATA_LOADED:
            console.log('[PendingReviewsService] User data loaded, fetching pending reviews');
            this.loadPendingReviews();
            break;
          case AuthEventType.LOGGED_OUT:
          case AuthEventType.SESSION_EXPIRED:
            console.log('[PendingReviewsService] User logged out, clearing state');
            this.clearState();
            break;
        }
      })
    );
  }

  /**
   * Pending reviews are firm-scoped. When the user switches firm, drop the
   * stale state immediately (so banners/badges don't flash the previous
   * firm's count) and re-fetch for the new firm. Selected household no
   * longer applies — clear it.
   */
  private listenToFirmContextChanges(): void {
    this.subscriptions.push(
      this.organization_group_service.firmContextChanged$.subscribe(() => {
        console.log('[PendingReviewsService] Firm context changed, refreshing pending reviews');
        this.clearState();
        this.loadPendingReviews();
      })
    );
  }

  /**
   * Load all pending reviews (separation and classification)
   */
  public async loadPendingReviews(householdId?: number | null): Promise<void> {
    this.updateState({ is_loading: true });

    try {
      // Fetch separation reviews
      const separation_response = await this.fetchSeparationReviews(householdId);

      // Fetch classification reviews
      const classification_response = await this.fetchClassificationReviews(householdId);

      const new_state: Partial<PendingReviewsState> = {
        separation_reviews: separation_response.documents,
        separation_review_count: separation_response.totalCount ?? separation_response.documents.length,
        classification_reviews: classification_response.documents,
        classification_review_count: classification_response.totalCount ?? classification_response.documents.length,
        is_loading: false,
        last_fetched_at: new Date(),
        selected_household_id: householdId ?? null,
      };

      new_state.total_count = (new_state.separation_review_count ?? 0) + (new_state.classification_review_count ?? 0);

      this.updateState(new_state);
      console.log('[PendingReviewsService] Pending reviews loaded:', new_state);
    } catch (error) {
      console.error('[PendingReviewsService] Error loading pending reviews:', error);
      this.updateState({ is_loading: false });
    }
  }

  /**
   * Refresh pending reviews - alias for loadPendingReviews for semantic clarity
   */
  public refresh(): Promise<void> {
    return this.loadPendingReviews();
  }

  /**
   * Fetch separation reviews from API (or dummy data if USE_DUMMY_DATA is true)
   */
  private async fetchSeparationReviews(householdId?: number | null): Promise<SeparationReviewListResponse> {
    // Use dummy data for development/testing
    if (USE_DUMMY_DATA) {
      console.log('[PendingReviewsService] Using dummy separation review data');
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 500));
      return DUMMY_SEPARATION_REVIEW_RESPONSE;
    }

    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (householdId) {
        queryParams.append('householdId', householdId.toString());
      }
      const queryString = queryParams.toString();
      const url = `approval/separation-review${queryString ? `?${queryString}` : ''}`;

      const response = await this.api_service.call<SeparationReviewListResponse>(
        HttpMethod.GET,
        url
      );
      return response;
    } catch (error) {
      console.error('[PendingReviewsService] Error fetching separation reviews:', error);
      return { documents: [], totalCount: 0 };
    }
  }

  /**
   * Fetch pending review count for a specific organization.
   *
   * Used by org-overview screens (e.g. OSJ overview) that need per-firm
   * pending counts. Calls the same two approval endpoints as the firm-wide
   * banner, scoped via `organizationId`, and sums their `totalCount`s.
   * Does NOT mutate the shared `state_subject` — callers receive the
   * number directly so the firm-context banner stays untouched.
   */
  public async fetchPendingCountForOrganization(organizationId: number): Promise<number> {
    try {
      const [separationResp, classificationResp] = await Promise.all([
        this.fetchSeparationReviewsForOrganization(organizationId),
        this.fetchClassificationReviewsForOrganization(organizationId),
      ]);
      const separationCount = separationResp.totalCount ?? separationResp.documents.length;
      const classificationCount = classificationResp.totalCount ?? classificationResp.documents.length;
      return separationCount + classificationCount;
    } catch (error) {
      console.error('[PendingReviewsService] Error fetching pending count for org', organizationId, error);
      return 0;
    }
  }

  private async fetchSeparationReviewsForOrganization(organizationId: number): Promise<SeparationReviewListResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('organizationId', organizationId.toString());
      const url = `approval/separation-review?${queryParams.toString()}`;
      return await this.api_service.call<SeparationReviewListResponse>(HttpMethod.GET, url);
    } catch (error) {
      console.error('[PendingReviewsService] Error fetching separation reviews for org', organizationId, error);
      return { documents: [], totalCount: 0 };
    }
  }

  private async fetchClassificationReviewsForOrganization(organizationId: number): Promise<ClassificationReviewListResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('organizationId', organizationId.toString());
      const url = `approval/categorization-review?${queryParams.toString()}`;
      return await this.api_service.call<ClassificationReviewListResponse>(HttpMethod.GET, url);
    } catch (error) {
      console.error('[PendingReviewsService] Error fetching classification reviews for org', organizationId, error);
      return { documents: [], totalCount: 0 };
    }
  }

  /**
   * Fetch classification reviews from API
   */
  private async fetchClassificationReviews(householdId?: number | null): Promise<ClassificationReviewListResponse> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (householdId) {
        queryParams.append('householdId', householdId.toString());
      }
      const queryString = queryParams.toString();
      const url = `approval/categorization-review${queryString ? `?${queryString}` : ''}`;

      const response = await this.api_service.call<ClassificationReviewListResponse>(
        HttpMethod.GET,
        url
      );
      return response;
    } catch (error) {
      console.error('[PendingReviewsService] Error fetching classification reviews:', error);
      return { documents: [], totalCount: 0 };
    }
  }

  /**
   * Get current state snapshot
   */
  public getState(): PendingReviewsState {
    return this.state_subject.getValue();
  }

  /**
   * Get current pending review count
   */
  public getPendingReviewCount(): number {
    return this.state_subject.getValue().total_count;
  }

  /**
   * Check if there are any pending reviews
   */
  public hasPendingReviews(): boolean {
    return this.getPendingReviewCount() > 0;
  }

  /**
   * Get currently selected household ID
   */
  public getSelectedHouseholdId(): number | null {
    return this.state_subject.getValue().selected_household_id;
  }

  private updateState(partial_state: Partial<PendingReviewsState>): void {
    const current_state = this.state_subject.getValue();
    this.state_subject.next({ ...current_state, ...partial_state });
  }

  private clearState(): void {
    this.state_subject.next(INITIAL_STATE);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
